import { logError } from '@ardoq/logging';
import {
  RelationshipDiagramCollapseGroupPayload,
  RelationshipDiagramSetCollapsedGroupIdsPayload,
} from '../actions';
import {
  aggregateReferences,
  getDistinctReferenceKey,
  getGraphEdgeLabelForViewpointMode,
  getIsShowReferenceTypeApplied,
} from '@ardoq/graph';
import { WILDCARD } from '@ardoq/common-helpers';
import { isScenarioMode } from 'models/utils/scenarioUtils';
import {
  getAddedAndProcessedNodes,
  getNewCollapsedGroupIdsAfterToggle,
} from './collapseGroupUtils';
import { toViewModel } from './viewModelFromGraph';
import { getPureGraphItems, getViewpointModeLabelsData } from './utils';
import { ModernizedBlockDiagramSourceDataShape } from 'tabview/blockDiagram/types';
import { getGraphComponentLegendProps } from 'tabview/graphComponent/graphComponentLegendUtils';
import { getRawGraphWithScenarioData } from 'tabview/relationshipsDiagramsViewModel/getRawGraphWithScenarioData';
import { getGraphData } from 'tabview/relationshipsDiagramsViewModel/getGraphData';
import { getModernizedBlockDiagramRepresentationData } from 'tabview/blockDiagram/view/yFilesExtensions/modernized/modernizedBlockDiagramRepresentationData';
import { filterOperations } from '@ardoq/core';

type RebuildViewModelArgs = {
  state: ModernizedBlockDiagramSourceDataShape;
  collapsedGroupIds: string[];
  expandedStateChangingGroupId: string;
};
const rebuildViewpointViewModel = ({
  state,
  collapsedGroupIds,
  expandedStateChangingGroupId,
}: RebuildViewModelArgs) => {
  const {
    graphModel,
    loadedScenarioData,
    showScopeRelated,
    scenarioRelatedComponents,
    context,
    loadedGraph,
    viewId,
    viewInterface,
    viewSettings,
    scopeDiffState: { scopeDiff },
    scopeDataParams,
    i18nLocale,
    users,
  } = state;

  const collapsedGroupIdsSet = new Set(collapsedGroupIds);
  const isInScopeDiffMode = Boolean(scopeDiff);

  const rawGraph = getRawGraphWithScenarioData({
    viewInterface,
    loadedScenarioData,
    showScopeRelated,
    scenarioRelatedComponents,
    graphModel,
    viewId,
    loadedGraph,
    isInScopeDiffMode: Boolean(scopeDiff),
    collapsedGroupIds: collapsedGroupIdsSet,
    shouldCollapseGraph: true,
    userDefinedGroupingRules: viewInterface.getAllGroupingRules(),
    i18nLocale,
  });

  const {
    componentMap,
    groupMap,
    referenceList: collapsedReferenceList,
    componentIdsOfCollapsedGroups,
    uncollapsedNodes,
    errors,
    hasClones,
  } = rawGraph;

  const {
    urlFieldValuesByComponentId,
    urlFieldValuesByReferenceId,
    representationDataByComponentId,
    nodeColorsByGraphItemId,
    conditionalFormattingState,
    edgeColorsByReferenceModelId,
  } = getGraphData({
    ...rawGraph,
    viewInterface,
    getRepresentationData: (componentId: string) =>
      getModernizedBlockDiagramRepresentationData(
        viewInterface,
        componentId,
        viewSettings.componentStyle
      ),
  });

  const isShowReferenceTypeApplied = getIsShowReferenceTypeApplied(
    viewInterface.getAllReferenceLabelFilters()
  );

  const aggregatedReferenceList = aggregateReferences({
    viewInterface,
    referenceList: collapsedReferenceList,
    getDistinctReferenceKey,
    collapsedGroupIds: collapsedGroupIdsSet,
    isInScopeDiffMode,
    urlFieldValuesByReferenceId: urlFieldValuesByReferenceId,
    getLabel: reference =>
      getGraphEdgeLabelForViewpointMode(
        viewInterface,
        reference,
        i18nLocale,
        isShowReferenceTypeApplied
      ).mainLabel ?? '',
    i18nLocale,
    ...uncollapsedNodes,
  });

  // Edge label data is indexed by graph edge id, hence it shall be composed after
  // reference aggregation (which creates new graph item ids for aggregated edges)
  const { nodeLabelsData, edgeLabelsData } = getViewpointModeLabelsData({
    viewInterface,
    componentMap,
    groupMap,
    rawReferences: aggregatedReferenceList,
    isShowReferenceTypeApplied,
    i18nLocale,
  });

  const { graphNodes, groupNodes, edges } = getPureGraphItems({
    i18nLocale,
    viewInterface,
    groupMap,
    componentMap,
    rawReferences: aggregatedReferenceList,
    componentIdsOfCollapsedGroups,
    nodeLabelsData,
    edgeLabelsData,
    urlFieldValuesByComponentId,
    urlFieldValuesByReferenceId,
    representationDataByComponentId,
    nodeColorsByGraphItemId,
    conditionalFormattingState,
    edgeColorsByReferenceModelId,
    rawCollapsedGroupIds: collapsedGroupIdsSet,
    scopeDiff,
    contextComponentId: context.componentId,
  });

  const formattingFilters = (scopeDataParams?.formattingFilters ?? []).filter(
    filterOperations.isStrictlyFormattingFilter
  );

  const legendProps = getGraphComponentLegendProps({
    componentMap,
    groupMap,
    edges: aggregatedReferenceList,
    graphInterface: viewInterface,
    formattingFilters,
    scopeDiff,
    displayShapesInsteadOfIcons: viewSettings.componentStyle === 'shape',
    activeDiffMode: viewSettings.activeDiffMode,
    i18nLocale,
    users,
    isNewConditionalFormatting: true,
  });

  const nextState: ModernizedBlockDiagramSourceDataShape = {
    ...state,
    uiPropsAndCallbacks: { legendProps },
    componentIdsOfCollapsedGroups,
    expandedStateChangingGroupId,
    errors,
    hasClones,
    nodes: toViewModel([...graphNodes.values()]),
    edges: toViewModel(edges),
    groups: toViewModel([...groupNodes.values()]),
  };

  const { nodes, groups } = nextState;

  const { addedNodes, updatedNodes, addedGroups, updatedGroups } =
    getAddedAndProcessedNodes({ nodes, groups, expandedStateChangingGroupId });

  return {
    ...nextState,
    nodes: { ...nodes, add: addedNodes, update: updatedNodes },
    groups: {
      ...groups,
      add: addedGroups,
      update: updatedGroups,
    },
    rawGraph,
  };
};

const applyCollapsedGroupsUpdate = <
  TState extends ModernizedBlockDiagramSourceDataShape,
>(
  state: TState,
  collapsedGroupIds: string[],
  expandedStateChangingGroupId: string
): TState => {
  const newState = rebuildViewpointViewModel({
    state,
    collapsedGroupIds,
    expandedStateChangingGroupId,
  });

  return {
    ...state,
    ...newState,
    viewSettings: { ...state.viewSettings, collapsedGroupIds },
  };
};

export const toggleCollapseGroupReducerForViewpointMode = <
  TState extends ModernizedBlockDiagramSourceDataShape,
>(
  state: TState,
  { groupId }: RelationshipDiagramCollapseGroupPayload
): TState => {
  const { groups, allGroupIds } = state;
  const group = groups.byId.get(groupId);
  if (!group) {
    logError(Error('Cannot find group after toggling expansion.'), null, {
      groupId,
      groupIds: Array.from(groups.byId.keys()),
      isScenarioMode: isScenarioMode(),
    });
    return state;
  }

  const collapsedGroupIds = getNewCollapsedGroupIdsAfterToggle({
    oldCollapsedGroupIds: state.viewSettings.collapsedGroupIds,
    allGroupIds,
    toggledGroup: group,
  });

  return applyCollapsedGroupsUpdate(state, collapsedGroupIds, group.id);
};

export const collapseAllGroupsReducerForViewpointMode = <
  TState extends ModernizedBlockDiagramSourceDataShape,
>(
  state: TState
): TState => applyCollapsedGroupsUpdate(state, [WILDCARD], WILDCARD);

export const expandAllGroupsReducerForViewpointMode = <
  TState extends ModernizedBlockDiagramSourceDataShape,
>(
  state: TState
): TState => applyCollapsedGroupsUpdate(state, [], WILDCARD);

export const setCollapsedGroupIdsReducerForViewpointMode = <
  TState extends ModernizedBlockDiagramSourceDataShape,
>(
  state: TState,
  { collapsedGroupIds }: RelationshipDiagramSetCollapsedGroupIdsPayload
): TState => applyCollapsedGroupsUpdate(state, collapsedGroupIds, WILDCARD);
